import { Slot } from '@radix-ui/react-slot';
import { CSSProperties, forwardRef, ReactNode, Ref } from 'react';
import type { FlexProps } from './flex.component';
import { Flex } from './flex.component';

interface BleedSpecificAmount {
	/**
	 * Value to represent much the overflow should be.
	 */
	bleedAmount: string;
	/**
	 * "viewportWide" cannot be used simultaneously as "bleedAmount".
	 */
	viewportWide?: never;
}

interface BleedFull {
	/**
	 * "bleedAmount" cannot be used simultaneously as "viewportWide".
	 */
	bleedAmount?: never;
	/**
	 * Determine if the bleed should be to end of viewport.
	 */
	viewportWide: boolean;
}

export type BleedProps = {
	/**
	 * Merges its props onto its immediate child.
	 */
	asChild?: boolean;
	children?: ReactNode;
} & (BleedSpecificAmount | BleedFull);

function Bleed({ bleedAmount, asChild, viewportWide, ...props }: BleedProps, ref: Ref<HTMLDivElement>): JSX.Element {
	const Comp = asChild ? Slot : Flex;

	const { style: unknownStyle, ...rest } = props as unknown as FlexProps<'div'>;
	const style: CSSProperties = viewportWide
		? {
				width: '100vw',
				height: undefined,
				position: 'relative',
				left: '50%',
				right: '50%',
				marginLeft: '-50vw',
				marginRight: '-50vw',
				alignItems: undefined,
				...unknownStyle,
			}
		: {
				marginLeft: `-${bleedAmount}`,
				marginRight: `-${bleedAmount}`,
				width: undefined,
				height: undefined,
				alignItems: undefined,
				...unknownStyle,
			};

	return (
		<div
			style={{
				width: '100%',
			}}
		>
			<Comp style={style} ref={ref} {...rest} />
		</div>
	);
}

/**
 * When wrapping your content with `<Bleed>`, it will be slightly wider than the container and will overflow on both sides.
 */
const _Bleed = forwardRef(Bleed);

export { _Bleed as Bleed };

Bleed.displayName = 'CellulaBleed';
